import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  CircularProgress,
} from '@mui/material'
import { ArrowBack } from '@mui/icons-material'
import { authApi } from '@api/endpoints/auth'
import { getSaveFriendlyError } from '@utils/errorHandling'

export default function VerifyResetCodePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const phoneNumber = location.state?.phoneNumber || ''

  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Redirect if no phone number
  if (!phoneNumber) {
    navigate('/forgot-password')
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!code.trim()) {
      setError('Please enter the verification code')
      return
    }

    if (code.length !== 7) {
      setError('Verification code must be 7 digits')
      return
    }

    setIsLoading(true)

    try {
      const response = await authApi.verifyResetCode({
        phoneNumber,
        code,
      })

      // Navigate to reset password page with token
      navigate('/reset-password', {
        state: {
          phoneNumber,
          token: response.token,
          method: 'sms',
        },
      })
    } catch (err: any) {
      const friendlyError = getSaveFriendlyError(err)
      setError(friendlyError.message || 'Invalid or expired verification code. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = async () => {
    setIsLoading(true)
    setError(null)

    try {
      await authApi.forgotPassword({
        phoneNumber,
        method: 'sms',
      })

      setError(null)
      alert('A new verification code has been sent to your phone.')
    } catch (err: any) {
      const friendlyError = getSaveFriendlyError(err)
      setError(friendlyError.message || 'Failed to resend code. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          width: '100%',
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center' }}>
          Verify Code
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Enter the 7-digit code sent to {phoneNumber}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Verification Code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 7))}
            disabled={isLoading}
            autoFocus
            sx={{ mb: 3 }}
            placeholder="1234567"
            inputProps={{
              maxLength: 7,
              pattern: '[0-9]*',
              inputMode: 'numeric',
            }}
            helperText="Enter the 7-digit code from your SMS"
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading || code.length !== 7}
            sx={{ mb: 2 }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Verifying...
              </>
            ) : (
              'Verify Code'
            )}
          </Button>

          <Button
            fullWidth
            variant="text"
            onClick={handleResend}
            disabled={isLoading}
            sx={{ mb: 1 }}
          >
            Resend Code
          </Button>

          <Button
            fullWidth
            variant="text"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/forgot-password')}
            disabled={isLoading}
          >
            Back
          </Button>
        </Box>
      </Paper>
    </Box>
  )
}
